"use client";

import Link from "next/link";
import { forwardRef, useEffect, useImperativeHandle, useRef, useState, type FormEvent } from "react";
import { ArrowRight, ArrowUp } from "lucide-react";
import { useChat, type Faq } from "./ChatProvider";
import { IonOrb } from "./IonOrb";
import styles from "./FaqChat.module.css";

/** How many questions to offer under the greeting. The rest come as follow-ups. */
const STARTERS = 4;

/** The conversation, question box and suggested questions: the same in the FAQ section and the floating chat. */
export const ChatBody = forwardRef<HTMLInputElement, { faqs: Faq[]; name?: string; compact?: boolean }>(function ChatBody(
  { faqs, name = "ION", compact },
  inputRef,
) {
  const { log, typing, busy, ask, send, startContact, replies, step, progress } = useChat();
  const [text, setText] = useState("");
  const logRef = useRef<HTMLDivElement>(null);
  const fieldRef = useRef<HTMLInputElement>(null);
  useImperativeHandle(inputRef, () => fieldRef.current as HTMLInputElement);
  // Whether the visitor has used this copy of the chat: the page has two.
  const engaged = useRef(false);

  // Keep the question and the start of its answer in view, so an answer is read
  // from its first word. The contact questions are short, so they follow the end.
  // Scrolls the chat only, never the page, and not when follow-ups arrive.
  const count = log.length;
  const lastPrivate = log[count - 1]?.private;
  useEffect(() => {
    const el = logRef.current;
    if (!el || !engaged.current) return;
    const still = window.matchMedia("(prefers-reduced-motion: reduce)").matches;
    const asked = [...el.querySelectorAll<HTMLElement>(`.${styles.you}`)].pop();
    const top = lastPrivate || !asked ? el.scrollHeight : asked.offsetTop - 12;
    el.scrollTo({ top, behavior: still ? "auto" : "smooth" });
    // `busy` too: the answer may have grown since its first words arrived.
  }, [count, typing, busy, lastPrivate]);

  // A tapped button is disabled while ION answers, which drops the focus. Bring it
  // back to the box, except on touch screens, where that would open the keyboard.
  useEffect(() => {
    if (busy || !engaged.current || !window.matchMedia("(hover: hover)").matches) return;
    if (document.activeElement === document.body) fieldRef.current?.focus({ preventScroll: true });
  }, [busy]);

  function onSubmit(e: FormEvent) {
    e.preventDefault();
    if (busy || !text.trim()) return;
    engaged.current = true;
    send(text, faqs);
    setText("");
  }

  function talk() {
    engaged.current = true;
    startContact();
  }

  /** A list of questions to tap, under the greeting or an answer. */
  const questions = (list: string[]) => (
    <div className={styles.next}>
      {list.map((q) => (
        <button
          key={q}
          type="button"
          onClick={() => {
            engaged.current = true;
            ask(q, faqs, faqs.find((f) => f.q === q));
          }}
          disabled={busy}
        >
          <span>{q}</span>
          <ArrowRight className="h-3.5 w-3.5" aria-hidden="true" />
        </button>
      ))}
    </div>
  );

  return (
    <>
      <div
        ref={logRef}
        className={`${styles.log} ${compact ? styles.logCompact : ""}`}
        role="log"
        aria-live="polite"
        aria-label={`Questions for ${name}`}
        data-clarity-mask="True"
      >
        {log.map((m, i) =>
          m.from === "you" ? (
            <div key={i} className={styles.you}>
              <strong className="sr-only">You</strong>
              <p>{m.text}</p>
            </div>
          ) : (
            <div key={i} className={styles.bot}>
              <span className={styles.avatar}>
                <IonOrb size={22} still />
              </span>
              <div className={styles.body}>
                <strong className="sr-only">{name}</strong>
                <p>{m.text}</p>
                {m.sources && m.sources.length > 0 && (
                  <p className={styles.sources}>
                    Source:{" "}
                    {m.sources.map((s, n) => (
                      <span key={s.url}>
                        {n > 0 && ", "}
                        <Link href={s.url}>{s.title}</Link>
                      </span>
                    ))}
                  </p>
                )}
                {m.link && (
                  <button type="button" className={styles.more} onClick={talk} disabled={busy || step !== null}>
                    Book a walkthrough
                  </button>
                )}
                {step === null && i === 0 && log.length === 1 && questions(faqs.slice(0, STARTERS).map((f) => f.q))}
                {step === null && m.next && m.next.length > 0 && questions(m.next)}
                {step === null && m.nextLoading && (
                  <div className={styles.nextLoading} aria-hidden="true">
                    <i />
                    <i />
                    <i />
                  </div>
                )}
                {step === null && m.offer && (
                  <button type="button" className={styles.offer} onClick={talk} disabled={busy}>
                    Talk to the team
                  </button>
                )}
              </div>
            </div>
          ),
        )}
        {typing && (
          <div className={`${styles.bot} ${styles.typing}`} aria-label={`${name} is typing`}>
            <span className={`${styles.avatar} ${styles.thinking}`}>
              <IonOrb size={22} />
            </span>
            <span className={styles.dots}>
              <i />
              <i />
              <i />
            </span>
          </div>
        )}
      </div>

      <div className={styles.foot}>
        {progress && <p className={styles.progress}>{progress}</p>}
        {replies.length > 0 && (
          <div className={styles.chips}>
            {replies.map((r) => (
              <button
                key={r}
                type="button"
                className={r === "Cancel" ? undefined : styles.primary}
                onClick={() => {
                  engaged.current = true;
                  send(r, faqs);
                }}
                disabled={busy}
              >
                {r}
              </button>
            ))}
          </div>
        )}

        <form className={styles.ask} onSubmit={onSubmit}>
          <input
            ref={fieldRef}
            value={text}
            onChange={(e) => setText(e.target.value)}
            placeholder={step?.placeholder ?? "Ask a question"}
            aria-label={step?.placeholder ?? "Ask a question"}
            autoComplete={step?.autoComplete ?? "off"}
            inputMode={step?.key === "email" ? "email" : undefined}
            maxLength={500}
            data-clarity-mask="True"
          />
          <button type="submit" aria-label="Send" disabled={!text.trim() || busy}>
            <ArrowUp className="h-4 w-4" />
          </button>
        </form>

        <div className={styles.meta}>
          <p className={styles.note}>
            {step ? (
              <>
                We only use these details to reply to you. <Link href="/privacy">Privacy policy</Link>
              </>
            ) : (
              "AI answers from Trialion’s website. Please don’t share patient or health details."
            )}
          </p>
          {replies.length === 0 && (
            <button type="button" className={styles.talk} onClick={talk} disabled={busy}>
              Talk to the team
            </button>
          )}
        </div>
      </div>
    </>
  );
});
